import isEmpty from "lodash/isEmpty";
import { useEffect, useState } from "react";
import { useGetPackagingMaterial } from "../../../../api/static-data";
import { images } from "../../../../assets/images";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "../../../../components/ui/common/accordion";
import { Card } from "../../../../components/ui/common/card";
import { warningMessage } from "../../../../helpers/ErrorHandler";
import PackagingLevel from "./PackagingLevel";

interface PackagingSectionProps {
  ingredientId: string;
  supplierId: string;
  refetchPackagingData: () => void;
  ingredientPackageData: any;
  dataView?: boolean;
}

/**
 * "Packaging information" card — Tailwind rebuild of the legacy
 * `IngredientPackagingView`
 * (src/screens/ingredients/ingredientRegister/IngredientPackageing.tsx),
 * same auto-enable logic and the same 4 `PackagingLevel` instances (single/
 * sales/bulk/transport unit), just off Semantic UI (`Accordion`/`Image`) and
 * onto the Tailwind equivalents.
 */
const PackagingSection = ({
  ingredientId,
  supplierId,
  refetchPackagingData,
  ingredientPackageData,
  dataView = false
}: PackagingSectionProps) => {
  const [accordionValue, setAccordionValue] = useState<string>("");
  const [guideValue, setGuideValue] = useState<string>("guide");

  const { data: packagingTypeData } = useGetPackagingMaterial();

  useEffect(() => {
    const hasPackagingTypeData = !isEmpty(packagingTypeData);
    const hasPackagingData =
      ingredientPackageData &&
      (ingredientPackageData.singleUnitPackaging?.length > 0 ||
        ingredientPackageData.salesUnitPackaging?.length > 0 ||
        ingredientPackageData.bulkUnitPackaging?.length > 0 ||
        ingredientPackageData.transportUnitPackaging?.length > 0);
    setAccordionValue(hasPackagingTypeData && (hasPackagingData || dataView) ? "content" : "");
  }, [packagingTypeData, ingredientPackageData, dataView]);

  return (
    <Card className="tw-gap-3 tw-py-4">
      <Accordion
        type="single"
        collapsible
        value={accordionValue}
        onValueChange={value => {
          if (isEmpty(ingredientId)) {
            warningMessage("Please create Ingredient");
            return;
          }
          setAccordionValue(value);
        }}
      >
        <AccordionItem value="content" className="tw-border-b-0">
          <AccordionTrigger className="tw-px-4 tw-py-0 hover:tw-no-underline">
            <span className="tw-text-sm tw-font-semibold tw-text-primary">Packaging Information</span>
          </AccordionTrigger>
          <AccordionContent className="tw-flex tw-flex-col tw-gap-4 tw-px-4">
            <Accordion type="single" collapsible value={guideValue} onValueChange={setGuideValue}>
              <AccordionItem value="guide" className="tw-border-b-0">
                <AccordionTrigger className="tw-py-1 tw-text-left tw-text-sm tw-font-normal tw-text-muted-foreground hover:tw-no-underline">
                  Please add information about how your ingredients are packaged at each level
                  following the GS1 guidelines. Use this image as a reference.
                </AccordionTrigger>
                <AccordionContent>
                  <img
                    src={images.PackingProcess}
                    alt="GS1 packaging guideline"
                    className="tw-max-w-full tw-rounded-md tw-border tw-border-border"
                  />
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <PackagingLevel
              packagingId={ingredientPackageData?._id || null}
              packagingType="single-unit"
              unitSizes={ingredientPackageData?.unitSize || 0}
              totalWeightOfItem={ingredientPackageData?.totalWeightBaseUnit || 0}
              packagingData={ingredientPackageData?.singleUnitPackaging || []}
              packagingTypeData={packagingTypeData || []}
              ingredientId={ingredientId}
              supplierId={supplierId}
              refetchPackagingData={refetchPackagingData}
              dataView={dataView}
            />
            <PackagingLevel
              packagingId={ingredientPackageData?._id || null}
              packagingType="sales-unit"
              unitSizes={ingredientPackageData?.numberOfBaseUnits || 0}
              totalWeightOfItem={ingredientPackageData?.totalWeightSalesOfPack || 0}
              packagingData={ingredientPackageData?.salesUnitPackaging || []}
              packagingTypeData={packagingTypeData || []}
              ingredientId={ingredientId}
              supplierId={supplierId}
              refetchPackagingData={refetchPackagingData}
              dataView={dataView}
            />
            <PackagingLevel
              packagingId={ingredientPackageData?._id || null}
              packagingType="bulk-unit"
              unitSizes={ingredientPackageData?.numberOfPacks || 0}
              totalWeightOfItem={ingredientPackageData?.totalWeightBulkPack || 0}
              packagingData={ingredientPackageData?.bulkUnitPackaging || []}
              packagingTypeData={packagingTypeData || []}
              ingredientId={ingredientId}
              supplierId={supplierId}
              refetchPackagingData={refetchPackagingData}
              dataView={dataView}
            />
            <PackagingLevel
              packagingId={ingredientPackageData?._id || null}
              packagingType="transport-unit"
              unitSizes={ingredientPackageData?.numberOfCases || 0}
              totalWeightOfItem={ingredientPackageData?.totalWeightTransportUnitPack || 0}
              packagingData={ingredientPackageData?.transportUnitPackaging || []}
              palletWidth={ingredientPackageData?.palletWidth}
              palletLength={ingredientPackageData?.palletLength}
              packagingTypeData={packagingTypeData || []}
              ingredientId={ingredientId}
              supplierId={supplierId}
              refetchPackagingData={refetchPackagingData}
              dataView={dataView}
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Card>
  );
};

export default PackagingSection;
